import React from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Heart, Sparkles, Smile, PawPrint, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import SEO from '../components/SEO';

const ServicesPage = () => {
  const iconMap = {
    scissors: Scissors,
    heart: Heart,
    droplet: Sparkles,
    sparkles: Sparkles,
    smile: Smile,
    'paw-print': PawPrint
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Dog Grooming Services Manchester | Full Groom, Spa & More"
        description="Professional dog grooming services in Manchester & Denton. Full grooms, puppy grooms, luxury spa treatments, Emmi-Pet teeth cleaning and more. All breeds welcome."
        keywords="dog grooming services Manchester, dog grooming Denton, full dog groom Manchester, puppy grooming Manchester, dog spa treatments Manchester, dog teeth cleaning Manchester, nail trimming dogs"
      />
      {/* Hero Section */}
      <section className="bg-white !pt-[0px] !pb-[0px]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center !py-[80px] !pb-[0px]">
            <h1 className="text-4xl md:text-5xl font-bold text-sage-900 mb-6">
              Professional Grooming Services
            </h1>
            <p className="text-lg text-sage-800 leading-relaxed mb-6">
              Every dog is unique, and our services are tailored to meet their individual needs. 
              From puppies to seniors, nervous dogs to show dogs – we treat them all with love and care.
            </p>
            <p className="text-lg text-sage-700 leading-relaxed">
              Our purpose-built salon blends spa luxury with the warmth of a homely atmosphere. 
              From soft lighting to gentle handling techniques and deluxe spa treatments – every element 
              is designed to prioritise your dog's comfort and well-being.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];

              return (
                <Card key={service.id} className="border-sage-200 hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-sage-500 to-sage-600 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-sage-900 mb-3">{service.name}</h3>
                    <p className="text-sage-600 mb-4 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
          <p className="text-center text-sage-500 text-sm mt-8 italic">Enquire for prices</p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                What's Included in Every Full Groom
              </h2>
              <p className="text-lg text-sage-600">
                We don't cut corners – your dog gets the full spa experience
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                {[
                'Thorough health check',
                'Luxury bath with premium shampoo',
                'Bow or bandana to take home',
                'Professional blow dry',
                'Full brush out & de-matting',
                'Breed-specific styling',
                'Nail trimming & filing',
                'Ear cleaning',
                'Hygiene trim',
                'Paw pad care',
                'Cute photo of your freshly groomed pup'].
                map((item, index) =>
                <div key={index} className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg">
                    <div className="w-6 h-6 bg-sage-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="text-white" size={16} />
                    </div>
                    <span className="text-sage-900 font-medium">{item}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/5fuip0sj_Photo%2022-08-2025%2C%2014%2038%2034.jpeg"
                  alt="Dog being groomed - nail trimming"
                  className="rounded-2xl shadow-xl max-w-md w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spa Treatments */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Spa Treatments
              </h2>
              <p className="text-lg text-sage-600">
                Treat your pup to some extra pampering with our add-on spa treatments
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="space-y-3">
                  {[
                    { name: 'Paw & Nose Balm', price: '£3' },
                    { name: 'Fresh Breath Foam', price: '£3' },
                    { name: 'Soothing Ear Cleanse/Pluck', price: '£5' },
                    { name: 'Standard Teeth Clean', price: '£5' },
                    { name: 'Luxury Facial Treatment', price: '£5' },
                    { name: 'Standard Nail Trim', price: '£10' },
                    { name: 'Anal Gland Express', price: '£10' },
                    { name: 'Deluxe Pawdicure', price: '£15' },
                    { name: 'Luxury Argan Oil Conditioner', price: '£5', from: true },
                    { name: 'Hydrating Mud Mask', price: '£10', from: true },
                    { name: 'Bubble Bath & Fluff', price: '£25', from: true },
                    { name: 'Emmi-Pet Deep Teeth Clean', price: '£50 / £40' },
                  ].map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-sage-100 last:border-0">
                      <span className="text-sage-800 font-medium">{item.name}</span>
                      <span className="text-sage-900 font-bold">
                        {item.from && <span className="text-sage-500 font-normal text-sm">from </span>}
                        {item.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spa Packages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Spa Packages
              </h2>
              <p className="text-lg text-sage-600">
                Bundle & save with our special spa packages
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Dream Package */}
              <Card className="border-sage-200 bg-gradient-to-br from-sage-50 to-white overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-sage-900">Dream Package</h3>
                    <span className="bg-sage-600 text-white px-4 py-1 rounded-full font-bold">£10</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      'Facial Treatment',
                      'Shine Spray',
                      'Luxury Conditioner',
                      'Puppuccino',
                      'Paw & Nose Balm',
                      'Bow or Bandana',
                      'Standard Teeth Clean',
                      'Photo'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="text-sage-600 flex-shrink-0" size={16} />
                        <span className="text-sage-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Birthday Package */}
              <Card className="border-sage-200 bg-gradient-to-br from-amber-50 to-white overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-sage-900">Birthday Package</h3>
                    <span className="bg-sage-600 text-white px-4 py-1 rounded-full font-bold">£10</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      'Facial Treatment',
                      'Fresh Breath Foam',
                      'Paw & Nose Balm',
                      'Birthday Photo',
                      'Bow / Bandana',
                      'Puppuccino'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="text-sage-600 flex-shrink-0" size={16} />
                        <span className="text-sage-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-sage-600 mt-8 italic">
              ✨ Look out for our Seasonal Spa Packages: Halloween, Christmas, Valentines, Easter & Summer
            </p>
          </div>
        </div>
      </section>

      {/* Puppuccino Menu */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Puppuccino Menu
              </h2>
              <p className="text-lg text-sage-600">
                A tasty treat for your pup after their pamper session
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="space-y-6">
                {[
                  { name: 'Classic Puppuccino', desc: 'Fresh whipped cream', price: '£1' },
                  { name: 'Peanut Butter Crunch', desc: 'Fresh whipped cream, doggy peanut butter', price: '£2' },
                  { name: 'Cookie Crumble', desc: 'Fresh whipped cream, doggy cookie topping', price: '£2' },
                ].map((item, index) => (
                  <div key={index} className="text-center pb-4 border-b border-sage-100 last:border-0 last:pb-0">
                    <h3 className="text-xl font-bold text-sage-900 mb-1">{item.name}</h3>
                    <p className="text-sage-600 italic mb-2">{item.desc}</p>
                    <span className="text-sage-800 font-bold">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emmi-Pet Teeth Cleaning */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div>
                <div className="inline-block bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  Good Health Starts with Good Teeth
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-6">
                  Emmi-Pet Teeth Cleaning
                </h2>
                <p className="text-lg text-sage-700 leading-relaxed mb-6">
                  The Emmi-Pet ultrasonic toothbrush is a completely silent, non-vibrating and non-invasive 
                  treatment. It deep cleans and helps to target staining, plaque & tartar build up. 
                  Healthy teeth, happy dog!
                </p>
                
                <div className="bg-sage-50 rounded-2xl p-6 mb-6">
                  <h3 className="font-bold text-sage-900 mb-4">Benefits:</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      'Removes staining',
                      'Targets bad breath',
                      'Reduces inflammation of gums',
                      'Prevents dental diseases',
                      'Works below the gum line',
                      'Silent & non-scrubbing motion'
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="text-sage-600 flex-shrink-0" size={18} />
                        <span className="text-sage-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1 mb-6">
                  <p className="text-sage-700 text-sm">Recommended from 6 months old</p>
                  <p className="text-sage-700 text-sm">Duration varies 15-30 minutes</p>
                  <p className="text-sage-700 text-sm">Maintain every 4-8 weeks</p>
                </div>

                <div className="flex gap-4">
                  <div className="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl px-6 py-3 text-center shadow-sm">
                    <p className="text-sage-600 text-sm mb-1">First Session</p>
                    <p className="font-bold text-sage-900 text-xl">£50</p>
                  </div>
                  <div className="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl px-6 py-3 text-center shadow-sm">
                    <p className="text-sage-600 text-sm mb-1">Top Ups</p>
                    <p className="font-bold text-sage-900 text-xl">£40</p>
                  </div>
                </div>
              </div>

              {/* Before & After Image */}
              <div>
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/0uraazco_24aa60a7-6716-49f7-9680-d09742b8b7cf.jpeg"
                  alt="Emmi-Pet teeth cleaning before and after results"
                  className="w-full rounded-2xl shadow-xl"
                />
                <p className="text-center text-sage-500 text-sm mt-3 italic">Before & After Emmi-Pet Treatment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Care */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_9f3fada2-34ee-435a-93bd-c381f4301c73/artifacts/5n4wyp6k_Photo%2022-08-2025%2C%2009%2029%2034.jpg"
                alt="Professional dog grooming in action at Pups & Dreams"
                className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover object-center" />

            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900">
                Gentle Care for Every Dog
              </h2>
              <p className="text-lg text-sage-700 leading-relaxed">
                Your dog will be treated with love, patience, and care at every visit. 
                We understand that every dog is different, and we adapt our approach to suit their personality.
              </p>
              <div className="space-y-4">
                <Card className="border-sage-200 bg-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-sage-900 mb-2">Nervous Dogs Welcome</h3>
                    <p className="text-sage-600">
                      We take extra time with anxious dogs, building trust and creating positive experiences.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-sage-200 bg-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-sage-900 mb-2">Senior Dog Specialists</h3>
                    <p className="text-sage-600">
                      Gentle handling and shorter sessions for older dogs who need extra comfort.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-sage-200 bg-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-sage-900 mb-2">Puppy Introduction Program</h3>
                    <p className="text-sage-600">
                      Building confidence from their first groom for a lifetime of happy spa visits.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
            Ready to Book?
          </h2>
          <p className="text-xl text-sage-700 mb-8 max-w-2xl mx-auto">
            Give your dog the grooming experience they deserve
          </p>
          <Link to="/contact">
            <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium px-8 rounded-md shadow-lg text-white">
              Book an Appointment
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default ServicesPage;